/**
 * Authentication Context
 * 
 * Provides authentication state and methods throughout the app.
 * Manages user session, login/logout, and user profile data.
 */

import { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  signInWithYouTube,
  getYouTubeSignInResult,
  fetchYouTubeChannelInfo,
  signOut,
  subscribeToAuthState,
} from '../services/auth';
import {
  createOrUpdateUser,
  getUserById,
  generateUniqueTag,
} from '../services/db';

// Create context
const AuthContext = createContext(null);

/**
 * Custom hook to use auth context
 * @returns {Object} Auth context value
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/**
 * Authentication Provider Component
 */
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Handle OAuth redirect result on app load
  useEffect(() => {
    const handleRedirectResult = async () => {
      try {
        const result = await getYouTubeSignInResult();
        if (result) {
          const { user, accessToken } = result;

          // Fetch YouTube channel information
          const channelInfo = await fetchYouTubeChannelInfo(accessToken);

          // Generate unique verified tag
          const verifiedTag = await generateUniqueTag(channelInfo.channelTitle);

          // Create or update user in database
          await createOrUpdateUser(user.uid, {
            uid: user.uid,
            email: user.email,
            displayName: channelInfo.channelTitle,
            avatarURL: channelInfo.thumbnailUrl,
            channelId: channelInfo.channelId,
            verifiedTag,
            messagePermissions: 'everyone', // Default permission
            blockedUsers: [],
            createdAt: new Date().toISOString(),
          });
        }
      } catch (err) {
        console.error('Redirect result error:', err);
        setError(err.message);
      }
    };

    handleRedirectResult();
  }, []);

  // Subscribe to auth state changes
  useEffect(() => {
    const unsubscribe = subscribeToAuthState(async (user) => {
      if (user) {
        setCurrentUser(user);
        // Fetch user profile from database
        try {
          const profile = await getUserById(user.uid);
          setUserProfile(profile);
        } catch (err) {
          console.error('Error fetching user profile:', err);
        }
      } else {
        setCurrentUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  /**
   * Initiate YouTube OAuth sign-in (redirect-based)
   */
  const login = async () => {
    try {
      setError(null);
      setLoading(true);

      // Initiate redirect to Google OAuth
      await signInWithYouTube();

      // Note: This will redirect the user away from the app
      // The redirect result will be handled by the useEffect above
    } catch (err) {
      console.error('Login initiation error:', err);
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  };

  /**
   * Sign out current user
   */
  const logout = async () => {
    try {
      setError(null);
      await signOut();
      setCurrentUser(null);
      setUserProfile(null);
    } catch (err) {
      console.error('Logout error:', err);
      setError(err.message);
    }
  };

  /**
   * Refresh user profile from database
   */
  const refreshProfile = async () => {
    if (!currentUser) return;
    try {
      const profile = await getUserById(currentUser.uid);
      setUserProfile(profile);
    } catch (err) {
      console.error('Error refreshing profile:', err);
    }
  };

  const value = {
    currentUser,
    userProfile,
    loading,
    error,
    login,
    logout,
    refreshProfile,
    isAuthenticated: !!currentUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
